// JOBE-0166.1: SuiteCard — collapsible suite card for PromptOps Suites (W7).
// JOBE-0166.2: Adds grouped metric sections in expanded body.

import { useState, useCallback } from 'react';
import { Card } from '../components/Card/Card.jsx';
import { PillChip } from '../components/PillChip/PillChip.jsx';
import { MetricSection } from './MetricSection.jsx';
import './SuiteCard.css';

// Canonical section order — always render in this order; omit absent sections.
export const SECTION_ORDER = ['write_quality', 'retrieval', 'hygiene', 'performance'];
export const SECTION_LABELS = {
  write_quality: 'Write Quality',
  retrieval:     'Retrieval',
  hygiene:       'Hygiene',
  performance:   'Performance',
};

/**
 * Group suite.metrics by section key.
 * @param {import('./mock-suites.js').Metric[]} metrics
 * @returns {Record<string, import('./mock-suites.js').Metric[]>}
 */
export function groupMetrics(metrics) {
  const groups = {};
  for (const m of metrics) {
    (groups[m.section] ??= []).push(m);
  }
  return groups;
}

/**
 * Format an ISO-8601 timestamp as a relative time string.
 * @param {string} iso
 * @returns {string} e.g. "3h ago", "2d ago"
 */
function relativeTime(iso) {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffH = Math.round(diffMs / 3_600_000);
  if (diffH < 1) return 'just now';
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  return `${diffD}d ago`;
}

/**
 * Count passing metrics in a suite.
 * A metric passes if current !== null and current >= target (or lower-is-better
 * semantics — current <= target for ms/ratio-lower). For the pass rate KPI tile
 * we simply count non-null current values as "measured" and apply >= target for
 * ratio/count, <= target for ms.
 */
function countPass(metrics) {
  let passing = 0;
  let total = 0;
  for (const m of metrics) {
    if (m.current === null || m.current === undefined) continue;
    total++;
    const lowerBetter = m.unit === 'ms' || m.id.includes('pollution') || m.id.includes('contamination') || m.id.includes('error_rate');
    const passes = lowerBetter ? m.current <= m.target : m.current >= m.target;
    if (passes) passing++;
  }
  return { passing, total };
}

/**
 * @param {object} props
 * @param {import('./mock-suites.js').Suite} props.suite
 * @param {boolean} [props.defaultExpanded=false]
 */
export function SuiteCard({ suite, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggle = useCallback(() => setExpanded((v) => !v), []);

  const { passing, total } = countPass(suite.metrics);

  // Click-to-copy suite ID (D15 — URN copy affordance).
  const handleIdClick = useCallback((e) => {
    e.stopPropagation();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(suite.id).catch(() => {});
    }
  }, [suite.id]);

  return (
    <Card id={suite.id} className="suite-card">
      <div
        className="suite-card-head"
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
      >
        <div className="suite-card-head-left">
          <span className="suite-name">{suite.name}</span>
          <PillChip label={suite.scope} />
          <button
            className="suite-id-copy"
            type="button"
            aria-label={`Copy suite ID: ${suite.id}`}
            onClick={handleIdClick}
            data-testid="suite-id-copy"
          >
            {suite.id}
          </button>
        </div>
        <div className="suite-card-head-right">
          <span className="suite-last-run" data-testid="suite-last-run">
            {relativeTime(suite.lastRunAt)}
          </span>
          <span className="suite-pass-rate" data-testid="suite-pass-rate">
            {passing}/{total} pass
          </span>
          <span className="suite-chevron" aria-hidden="true">
            {expanded ? '▾' : '▸'}
          </span>
        </div>
      </div>
      {expanded && (
        <div className="suite-card-body" data-testid="suite-card-body">
          {(() => {
            const groups = groupMetrics(suite.metrics);
            return SECTION_ORDER
              .filter((sec) => groups[sec]?.length)
              .map((sec) => (
                <MetricSection
                  key={sec}
                  section={sec}
                  label={SECTION_LABELS[sec]}
                  metrics={groups[sec]}
                />
              ));
          })()}
        </div>
      )}
    </Card>
  );
}
